import { extent } from 'd3-array';
import React, { useMemo } from 'react';

import { useThemeDefinition } from '@tanium/react-theme-definition';

import { useTickValues } from '../../hooks/useTickValues';
import { StringCoercible } from '../../utils/StringCoercible';
import { ScaleWithValueType, SupportedScale } from '../../utils/SupportedScale';
import { TickFormatter } from '../../utils/TickFormatter';
import { CreatePortalFn } from '../../utils/usePortal';
import { Axis, AxisOrientation } from '../Axis';
import { Grid } from '../Grid';
import { Group } from '../Group';
import { Text } from '../Text';
import { AxisLabelText, GridStroke } from './themeDefinitions';

export interface AxisRenderFnProps<T> {
  createPortal: CreatePortalFn;
  isAnimated: boolean;
  left: number;
  max: number;
  height?: number;
  width?: number;
  min: number;
  orientation: AxisOrientation;
  scale: ScaleWithValueType<T>;
  tickCount: number | undefined;
  tickFormatter: TickFormatter<T> | undefined;
  ticks: T[] | undefined;
  top: number;
}

export type AxisRenderFn<T> = (props: AxisRenderFnProps<T>) => React.ReactNode;

const defaultAxisRenderer = <T extends StringCoercible>({
  createPortal,
  isAnimated,
  left,
  max,
  height,
  width,
  min,
  orientation,
  scale,
  tickCount,
  tickFormatter,
  ticks,
  top,
}: AxisRenderFnProps<T>) => (
  <>
    <Group top={top} left={left}>
      <Axis
        createPortal={createPortal}
        isAnimated={isAnimated}
        portalLeft={left}
        portalTop={top}
        height={height}
        width={width}
        orientation={orientation}
        start={min}
        end={max}
        scale={scale}
        tickCount={tickCount}
        ticks={ticks}
        tickFormatter={tickFormatter}
      />
    </Group>
  </>
);

// TODO: these values will need to be customizable and/or dynamically set based on the actual axis
// sizes, with the consumer responsible for implicitly resizing the plot accordingly (via scale
// ranges)
const Y_AXIS_LABEL_X_OFFSET = -56;

export interface PlotProps<TX, TY> {
  /**
   * Content to render in the plot. This content will be positioned to render above the grid, but
   * underneath the axes.
   */
  children: React.ReactNode;
  /**
   * A function (analogous to ReactDOM.createPortal) that renders the given content in the
   * associated portal.
   */
  createPortal: CreatePortalFn;
  /**
   * Whether animations are enabled.
   */
  isAnimated: boolean;
  /**
   * A d3 scale used for values along the x-axis.
   */
  xScale: ScaleWithValueType<TX>;
  /**
   * A d3 scale used for values along the y-axis.
   */
  yScale: ScaleWithValueType<TY>;
  /**
   * The reserved height of the x-axis region within the plot.
   */
  xAxisHeight: number;
  /**
   * A custom render function for rendering the x-axis. If omitted, a default renderer is used
   * instead.
   */
  xAxisRenderer?: AxisRenderFn<TX>;
  /**
   * The approximate number of ticks to show along the x-axis. A grid line is drawn for each tick.
   * Defaults to 10.
   *
   * This value is ignored if `xTicks` is specified.
   */
  xTickCount?: number;
  /**
   * The set of (pre-scaled) values to use as ticks along the x-axis. A grid line is drawn for each
   * tick.
   *
   * This value takes priority over `xTickCount`.
   */
  xTicks?: TX[];
  /**
   * A custom render function for rendering the y-axis. If omitted, a default renderer is used
   * instead.
   */
  yAxisRenderer?: AxisRenderFn<TY>;
  /**
   * The reserved width of the y-axis region within the plot.
   */
  yAxisWidth: number;
  /**
   * The approximate number of ticks to show along the y-axis. A grid line is drawn for each tick.
   * Defaults to 10.
   *
   * This value is ignored if `yTicks` is specified.
   */
  yTickCount?: number;
  /**
   * The set of (pre-scaled) values to use as ticks along the y-axis. A grid line is drawn for each
   * tick.
   *
   * This value takes priority over `yTickCount`.
   */
  yTicks?: TY[];
  /**
   * An optional y-axis label.
   */
  yAxisLabel?: string;
  /**
   * An optional horizontal offset for y-axis label from the y axis. Defaults to -56px
   */
  yAxisLabelXOffset?: number;
  /**
   * A format function to use to format tick labels along the x-axis. By default, the scale's
   * built-in tick formatter is used (if available), or a fallback formatter if the scale does not
   * have its own tick formatter.
   */
  xTickFormatter?: TickFormatter<TX>;
  /**
   * A format function to use to format tick labels along the y-axis. By default, the scale's
   * built-in tick formatter is used (if available), or a fallback formatter if the scale does not
   * have its own tick formatter.
   */
  yTickFormatter?: TickFormatter<TY>;
}

export const getRangePoints = <T extends unknown>(
  scale: SupportedScale<T>,
): { min: number; mid: number; max: number } => {
  const [min = 0, max = 0] = extent(scale.range());
  const mid = (max - min) / 2 + min;

  return { min, mid, max };
};

export const Plot = <TX, TY>({
  children,
  createPortal,
  isAnimated,
  xAxisHeight,
  xAxisRenderer = defaultAxisRenderer,
  xScale,
  xTickCount,
  xTickFormatter,
  xTicks,
  yAxisLabel,
  yAxisLabelXOffset = Y_AXIS_LABEL_X_OFFSET,
  yAxisWidth,
  yAxisRenderer = defaultAxisRenderer,
  yScale,
  yTickCount,
  yTickFormatter,
  yTicks,
}: PlotProps<TX, TY>): JSX.Element => {
  const { min: xMin, max: xMax } = getRangePoints(xScale);
  const { min: yMin, mid: yMid, max: yMax } = getRangePoints(yScale);

  const xPositions = useTickValues({
    scale: xScale,
    tickCount: xTickCount,
    ticks: xTicks,
  });

  const yPositions = useTickValues({
    scale: yScale,
    tickCount: yTickCount,
    ticks: yTicks,
  });

  const [axisLabelTextFill, gridStrokeColor] = useThemeDefinition([AxisLabelText, GridStroke]);

  const plotBottom = yMax;
  const plotLeft = xMin;

  const xAxis = useMemo(
    () =>
      xAxisRenderer({
        createPortal,
        isAnimated,
        left: 0,
        top: plotBottom,
        height: xAxisHeight,
        max: xMax,
        min: xMin,
        orientation: 'bottom',
        scale: xScale,
        tickFormatter: xTickFormatter,
        tickCount: xTickCount,
        ticks: xTicks,
      }),
    [
      xAxisRenderer,
      createPortal,
      isAnimated,
      plotBottom,
      xAxisHeight,
      xMax,
      xMin,
      xScale,
      xTickFormatter,
      xTickCount,
      xTicks,
    ],
  );

  const yAxis = useMemo(
    () =>
      yAxisRenderer({
        createPortal,
        isAnimated,
        left: plotLeft,
        top: 0,
        width: yAxisWidth,
        max: yMax,
        min: yMin,
        orientation: 'left',
        scale: yScale,
        tickFormatter: yTickFormatter,
        tickCount: yTickCount,
        ticks: yTicks,
      }),
    [
      createPortal,
      isAnimated,
      plotLeft,
      yAxisRenderer,
      yAxisWidth,
      yMax,
      yMin,
      yScale,
      yTickCount,
      yTickFormatter,
      yTicks,
    ],
  );

  return (
    <>
      <Grid
        isAnimated={isAnimated}
        stroke={gridStrokeColor}
        xScale={xScale}
        xValues={xPositions}
        yScale={yScale}
        yValues={yPositions}
      />
      {children}
      {xAxis}
      {yAxis}
      {yAxisLabel && (
        <Text
          textAnchor="middle"
          verticalAnchor="end"
          top={yMid}
          left={plotLeft + yAxisLabelXOffset}
          fill={axisLabelTextFill}
          angle={-90}
        >
          {yAxisLabel}
        </Text>
      )}
    </>
  );
};
